/**
 * 🔦 **Where a house's wall really is, for anything hung on it** — issue #532.
 *
 * 🎯 **haugur's own screenshot, inside bug report #529:** the torch beside the
 * búr's door *"is at an angle and clipping through the door rather than sitting
 * on the wall."*
 *
 * ## 🔴 The one sentence that explains it
 *
 * **A torch is nailed to the BLOCK GRID. The house is a photographed MODEL
 * drawn over that grid. The two surfaces are not in the same place.**
 *
 * The house's footprint is 9 cells long, so its front block face is at −4.5 m
 * from the middle — and **the drawn gable is at −4.04 m.** So a torch hung on
 * the front of a house sat **46 cm out in front of the wall it looked attached
 * to**, floating, and beside the doorway it crossed the door.
 *
 * ## ⚠️ This is the carved board's bug, one object smaller
 *
 * 🔴 **The board took five positions and four wrong ones shipped**, every one a
 * number that measured *something that was not the wall under the board* — the
 * model's pivot, the model's box (which is the ROOF, because the turf eave
 * overhangs), the door before its matrices were updated, and the door's own
 * bounding box, whose most extreme vertex is a 5% lip nothing else shares.
 *
 * ✅ **So this measures the wall under each torch**, at every cell a torch can
 * occupy, with the board's own instrument — `nearestSurfaceInPatch`, which
 * `frontmostZInPatch` is now a three-line call into. **One loop, not two.**
 *
 * ## 🔴 Why the four walls each need their own numbers
 *
 * | | inset from the block face |
 * |---|---|
 * | the gables, above the ground row | **0.38 – 0.47 m** |
 * | the long sides, below the eave | **0.18 – 0.26 m** |
 * | the gable along the DOOR's own row | **0.16 – 0.36 m** — the door and its
 *   threshold stand proud of the wall beside them |
 *
 * ⚠️ **A single average would push a torch beside the door a fifth of a metre
 * INTO the door**, which is the reported fault arriving from the other side.
 *
 * ## Where the numbers come from
 *
 * `../data/house-walls.json`, **generated by `tools/gen-house-wall-insets.mjs`
 * from `bur.glb` itself.** ⚠️ **Not hand-written, and not to be hand-edited.**
 * The file records the model's byte length and `housewalls.test.ts` fails if
 * the model on disk has changed — so a replaced model cannot pass quietly.
 */

import wallData from '../data/house-walls.json' with { type: 'json' };
import { BUR_FOOTPRINT, burFacing, burFrame, burDoorFor, isBur, isBurPart } from './bur.ts';
import type { WorldPosition } from './coords.ts';
import { torchAnchor, type TorchFacing } from './torch.ts';

/**
 * Which wall of a house, **named by the model's own axis** rather than by left
 * and right.
 *
 * 🔴 **"Left" needs a viewpoint and this must not.** The board spent an
 * afternoon on the wrong side of a door because *left* was reasoned about
 * rather than looked at; a name that says `+x` cannot be read two ways.
 * `front` is the gable the door is in — the model's −z end.
 */
export type HouseWall = 'front' | 'back' | 'xPlus' | 'xMinus';

/** The model's byte length when the table was measured. */
export const HOUSE_WALL_MODEL_BYTES: number = wallData.modelBytes;

/** The model the table was measured from. */
export const HOUSE_WALL_MODEL: string = wallData.model;

/**
 * ⚠️ **A Map rather than an object indexed by a variable** — the pattern
 * `bur.ts`, `states.ts` and `blocks.ts` all use, and for the reason the linter
 * gives: `wall` is a name arriving from outside, and **a type is not a runtime
 * guarantee.** A Map cannot be reached through a prototype.
 */
const INSETS: ReadonlyMap<HouseWall, readonly (readonly (number | null)[])[]> = new Map([
  ['front', wallData.insets.front],
  ['back', wallData.insets.back],
  ['xPlus', wallData.insets.xPlus],
  ['xMinus', wallData.insets.xMinus],
]);

/**
 * 📏 **How far inside its block face one wall is drawn, at one cell**, in
 * metres — or `null` where **there is no wall there at all**, which is the
 * honest answer for the top corners: the turf roof has sloped away and the
 * block behind is invisible.
 *
 * @param wall which wall, in the model's own axes.
 * @param along the cell along that wall — **across the width for a gable**
 *   (0 to 4, left to right of the model's +x), **along the length for a side**
 *   (0 at the door's row).
 * @param height the cell's floor above the house's own floor, 0 to 4.
 */
export function houseWallInset(wall: HouseWall, along: number, height: number): number | null {
  const rows = INSETS.get(wall);
  if (rows === undefined) return null;
  if (!Number.isInteger(along) || !Number.isInteger(height)) return null;
  const row = rows.at(height);
  if (row === undefined || height < 0) return null;
  const value = row.at(along);
  return value === undefined || along < 0 ? null : value;
}

/**
 * 🔦 **How far a torch's foot must be pushed back to reach the wall it looks
 * nailed to**, in metres — **0 for every torch that is not on a house**, which
 * is nearly all of them and is the answer that changes nothing.
 *
 * ## 🔴 What it costs, because "unmeasured" is not "probably fine"
 *
 * {@link burDoorFor} runs the footprint derivation backwards and is **up to
 * 1200 block lookups.** ✅ **It is only reached when the block the torch hangs
 * on is actually part of a house**, which is a single lookup to rule out. Twenty
 * wall torches on houses in one chunk is about **1200 × 20 ≈ 24 000 lookups on
 * a rebuild** — against the 32 768 cells that rebuild already walks to mesh the
 * chunk. **It is a fraction of the work already being done, and it is only paid
 * where somebody has built.**
 *
 * ## ⬜ What it deliberately does not do
 *
 * ⚠️ **A torch STANDING on a house answers 0** and still floats above the turf,
 * because the roof slopes and its height is not one number per cell. **That is
 * a different shape of question** and it is written down in the issue rather
 * than guessed at here.
 *
 * ⚠️ **A house longer than {@link BUR_FOOTPRINT}`.length` answers 0** past the
 * ninth row. The first slice shipped 12 long and haugur placed some; the model
 * is drawn 9 long, so **there is no wall out there to measure** — the torch
 * keeps the position it has today rather than being moved to a made-up one.
 *
 * @param at the torch's own cell.
 * @param facing which way it leans — **not the wall it is on**, see `torch.ts`.
 * @param blockAt the world. ⚠️ **It answers air for a chunk that has not
 *   arrived**, so a house still streaming in gives 0 and the torch is drawn
 *   where it is today. That is a wrong position for a moment, not a wrong
 *   position for ever, because the chunk is meshed again when it lands.
 */
export function torchWallInset(
  at: WorldPosition,
  facing: TorchFacing,
  blockAt: (x: number, y: number, z: number) => number,
): number {
  if (facing === 'up') return 0;
  const anchor = torchAnchor(at, facing);
  const anchorId = blockAt(anchor.x, anchor.y, anchor.z);
  if (!isBur(anchorId) && !isBurPart(anchorId)) return 0;

  const door = burDoorFor(anchor, blockAt);
  if (door === undefined) return 0;
  const houseFacing = burFacing(blockAt(door.x, door.y, door.z));
  if (houseFacing === undefined) return 0;

  const { into, right } = burFrame(houseFacing);
  /* The anchor's place in the building's own frame: `d` rows in from the door,
     `w` across, `h` up. This is `cellAt` run backwards, and it is only a dot
     product because `into` and `right` are perpendicular unit axes. */
  const dx = anchor.x - door.x;
  const dz = anchor.z - door.z;
  const d = dx * into.x + dz * into.z;
  const w = dx * right.x + dz * right.z;
  const h = anchor.y - door.y;

  /* Which way the torch leans, in the building's frame. ⚠️ Taken as the step
     from the anchor to the torch rather than from a second copy of the compass
     table — `torchAnchor` already knows it. */
  const leanX = at.x - anchor.x;
  const leanZ = at.z - anchor.z;
  const outInto = leanX * into.x + leanZ * into.z;
  const outRight = leanX * right.x + leanZ * right.z;

  const half = (BUR_FOOTPRINT.width - 1) / 2;
  if (outInto === -1) return houseWallInset('front', w + half, h) ?? 0;
  if (outInto === 1) return houseWallInset('back', w + half, h) ?? 0;
  if (outRight === 1) return houseWallInset('xPlus', d, h) ?? 0;
  if (outRight === -1) return houseWallInset('xMinus', d, h) ?? 0;
  return 0;
}
